import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Wallet } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  approveExtraJob,
  extraJobPhotoUrl,
  fetchExtraJobPhotos,
  fetchOrgExtraJobs,
  rejectExtraJob,
  type CleaningExtraJob,
  type ExtraJobPayMode,
} from "@/lib/extraPaidWork";

interface ExtraPaidApprovalQueueProps {
  orgId: string | null;
}

export function ExtraPaidApprovalQueue({ orgId }: ExtraPaidApprovalQueueProps) {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState<CleaningExtraJob[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [payMode, setPayMode] = useState<ExtraJobPayMode>("hourly");
  const [employeeHourlyRate, setEmployeeHourlyRate] = useState("");
  const [clientHourlyRate, setClientHourlyRate] = useState("");
  const [employeeFixedAmount, setEmployeeFixedAmount] = useState("");
  const [clientFixedAmount, setClientFixedAmount] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [photos, setPhotos] = useState<{ id: string; url: string }[]>([]);

  const load = useCallback(async () => {
    if (!orgId) return;
    setIsLoading(true);
    try {
      const rows = await fetchOrgExtraJobs(orgId);
      setJobs(rows);
    } catch (error) {
      console.error("[ExtraPaidApprovalQueue]", error);
      toast.error(t("admin.extraQueue.loadError"));
    } finally {
      setIsLoading(false);
    }
  }, [orgId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const pending = jobs.filter((job) => job.approvalStatus === "pending_review");
  const selected = jobs.find((job) => job.id === selectedId) ?? pending[0] ?? null;

  useEffect(() => {
    if (!selected) {
      setPhotos([]);
      return;
    }
    setPayMode(selected.rates.payMode);
    setEmployeeHourlyRate(selected.rates.employeeHourlyRate?.toString() ?? "");
    setClientHourlyRate(selected.rates.clientHourlyRate?.toString() ?? "");
    setEmployeeFixedAmount(selected.rates.employeeFixedAmount?.toString() ?? "");
    setClientFixedAmount(selected.rates.clientFixedAmount?.toString() ?? "");
    setRejectReason("");
    let cancelled = false;
    const loadPhotos = async () => {
      const rows = await fetchExtraJobPhotos(selected.id);
      const withUrls = await Promise.all(
        rows.map(async (row) => ({
          id: row.id,
          url: (await extraJobPhotoUrl(row.storagePath)) ?? "",
        }))
      );
      if (!cancelled) setPhotos(withUrls.filter((row) => row.url));
    };
    void loadPhotos();
    return () => {
      cancelled = true;
    };
  }, [selected?.id]);

  const handleApprove = async () => {
    if (!selected) return;
    try {
      await approveExtraJob({
        jobId: selected.id,
        payMode,
        employeeHourlyRate: employeeHourlyRate ? Number(employeeHourlyRate) : null,
        clientHourlyRate: clientHourlyRate ? Number(clientHourlyRate) : null,
        employeeFixedAmount: employeeFixedAmount ? Number(employeeFixedAmount) : null,
        clientFixedAmount: clientFixedAmount ? Number(clientFixedAmount) : null,
      });
      toast.success(t("admin.extraQueue.approved"));
      setSelectedId(null);
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("admin.extraQueue.approveError"));
    }
  };

  const handleReject = async () => {
    if (!selected) return;
    try {
      await rejectExtraJob(selected.id, rejectReason);
      toast.success(t("admin.extraQueue.rejected"));
      setSelectedId(null);
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("admin.extraQueue.rejectError"));
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Wallet className="h-5 w-5 text-amber-600" />
          <CardTitle>{t("admin.extraQueue.title")}</CardTitle>
        </div>
        <CardDescription>
          {t("admin.extraQueue.description")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : pending.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("admin.extraQueue.empty")}</p>
        ) : (
          <div className="grid gap-4 lg:grid-cols-[220px_1fr]">
            <div className="space-y-2">
              {pending.map((job) => (
                <button
                  key={job.id}
                  type="button"
                  onClick={() => setSelectedId(job.id)}
                  className={`w-full rounded-md border p-2 text-left text-sm ${
                    selected?.id === job.id ? "border-amber-500 bg-amber-50 dark:bg-amber-950/40" : ""
                  }`}
                >
                  <p className="font-medium">{job.title}</p>
                  <p className="text-xs text-muted-foreground">{job.staffName || t("admin.extraQueue.worker")}</p>
                </button>
              ))}
            </div>
            {selected && (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  <Badge>{selected.locationAddress || t("admin.extraQueue.location")}</Badge>
                  <Badge variant="secondary">
                    {selected.durationMinutes != null
                      ? t("admin.extraQueue.minutes", { count: selected.durationMinutes })
                      : t("admin.extraQueue.timeNotSaved")}
                  </Badge>
                </div>
                {selected.workReport && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">{t("admin.extraQueue.workerReport")}</p>
                    <p className="text-sm whitespace-pre-wrap">{selected.workReport}</p>
                  </div>
                )}
                {photos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {photos.map((photo) => (
                      <img key={photo.id} src={photo.url} alt={t("admin.extraQueue.photoAlt")} className="h-24 w-full object-cover rounded-md" />
                    ))}
                  </div>
                )}
                <div className="grid gap-2">
                  <Label>{t("admin.extraQueue.billing")}</Label>
                  <Select value={payMode} onValueChange={(value) => setPayMode(value as ExtraJobPayMode)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hourly">{t("admin.extraQueue.hourly")}</SelectItem>
                      <SelectItem value="fixed">{t("admin.extraQueue.fixed")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {payMode === "hourly" ? (
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="number" step="0.01" placeholder={t("admin.extraQueue.employeeRate")} value={employeeHourlyRate} onChange={(e) => setEmployeeHourlyRate(e.target.value)} />
                    <Input type="number" step="0.01" placeholder={t("admin.extraQueue.clientRate")} value={clientHourlyRate} onChange={(e) => setClientHourlyRate(e.target.value)} />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="number" step="0.01" placeholder={t("admin.extraQueue.employeeAmount")} value={employeeFixedAmount} onChange={(e) => setEmployeeFixedAmount(e.target.value)} />
                    <Input type="number" step="0.01" placeholder={t("admin.extraQueue.clientAmount")} value={clientFixedAmount} onChange={(e) => setClientFixedAmount(e.target.value)} />
                  </div>
                )}
                <Textarea placeholder={t("admin.extraQueue.rejectReason")} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                <div className="flex gap-2">
                  <Button onClick={() => void handleApprove()}>{t("admin.extraQueue.approve")}</Button>
                  <Button variant="outline" onClick={() => void handleReject()}>
                    {t("admin.extraQueue.reject")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
